import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {ConfigService} from 'app/core/services/config.service';
import {RequestService} from 'app/core/services/request.service';
import {Subject} from 'rxjs/Subject';
import {UtilService} from 'app/core/services/util.service';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {AbstractControl, FormArray, FormBuilder} from '@angular/forms';

@Injectable()
export class RegisterService {
    public personalDetailsUpdate = new Subject<any>();
    public patchUsers = new Subject<any[]>();
    public getTerms = new ReplaySubject<any[]>();
    public isUsersStepValid = new Subject<boolean>();

    public countryDict = this.getCountryDict().shareReplay(1);
    public accountTypeDict = this.getAccountTypeDict().shareReplay(1);
    public companyHoldingTypeDict = this.getCompanyHoldingTypeDict().shareReplay(1);
    public companyRegistrationTypeDict = this.getCompanyRegistrationTypeDict().shareReplay(1);
    public roleTypeDict = this.getRoleTypeDict().shareReplay(1);

    constructor(
        private configService: ConfigService,
        private requestService: RequestService,
        private utilService: UtilService,
        private fb: FormBuilder
    ) {
    }

    formatData(details: any) {
        const obj = {
            // domains: details.domains ? details.domains.map(_ => {return { id: _ }}) : [],
            // categoryFamily: details.categoryFamily ? details.categoryFamily.map(_ => {return { id: _ }}) : []
        };

        let result = Object.assign({}, details);
        result = Object.assign(result, obj);
        result.registrationDetails.account = this.utilService.unflatten(
            result.registrationDetails.account
        );

        return result;
    }

    getCountryDict(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.dictionary.country.api;
        return this.requestService.get(url);
    }

    getGridFormRows(fields: any[], existingValues: any[] = [], controlLimit: number): any[] {
        let result = [];
        existingValues.forEach(_ => {
            const group = {};
            fields.forEach(field => {
                let key = field.id;
                group[key] = [_[key] ? _[key] : null];
            });
            result.push(this.fb.group(group));
        });

        result = result.slice(0, controlLimit);
        return result;
    }

    validateCheckboxRequiredOne(control: AbstractControl) {
        let valid = false;
        let fa = control as FormArray;

        for (let x = 0; x < fa.length; ++x) {
            if (fa.at(x).value) {
                valid = true;
                break;
            }
        }
        return valid ? null : {
            multipleCheckboxRequireOne: true
        };
    }

    getValuesMap(values: any[]) {
        const result = {};
        values.forEach(val => {
            result[val.id] = {
                id: val.id,
                label: val.label,
                value: val.value,
                valueOrder: val.valueOrder
            };
        });

        return result;
    }

    parseGridContractDetails(controlValues: any[]) {
        const result = [];
        let rowCount: number;
        controlValues.forEach(_ => {
            _.value = JSON.parse(_.value);
        });

        rowCount = controlValues[0].value.length;

        for (let i = 0; i < rowCount; i++) {
            const value = {};
            controlValues.forEach(controlValue => {
                value[controlValue.id] = controlValue.value[i];
            });

            result.push(value);
        }

        return result;
    }

    getStatesByCountryDict(country: string): Observable<any> {
        let url =
            this.configService.get('server').sahara +
            this.configService.get('endpoints').sahara.dictionary.statesByCountry.api +
            '/' +
            country;
        return this.requestService.get(url, {verbose: true, type: 'api'});
    }

    getPatternCodeDict(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.dictionary.patternList.api;
        return this.requestService.get(url, {});
    }

    getAccountTypeDict(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.public.accountType.api;
        return this.requestService.get(url);
    }

    getCompanyHoldingTypeDict(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.public.companyHoldingType.api;
        return this.requestService.get(url);
    }

    getCompanyRegistrationTypeDict(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.public.companyRegistrationType.api;
        return this.requestService.get(url).share();
    }

    getRoleTypeDict(): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.public.userRoleType.api;
        return this.requestService.get(url);
    }

    stateAdd(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.stateAdd.api;
        return this.requestService.post(url, data, false, null, {'X-Api-Recaptcha': data['captcha']});
    }

    stateEdit(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.stateEdit.api;
        return this.requestService.post(url, data);
    }

    acknowledge(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.acknowledge.api;
        return this.requestService.post(url, data);
    }

    delegatedAcknowledge(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.delegatedAcknowledge.api;
        return this.requestService.post(url, data);
    }

    delegate(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.delegate.api;
        return this.requestService.post(url, data);
    }

    terms(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.terms.api;
        return this.requestService.post(url, data);
    }

    delegatedTerms(data: any): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.delegatedTerms.api;
        return this.requestService.post(url, data);
    }

    getState(id: string): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.getState.api +
            '/' +
            id;
        return this.requestService.get(url);
    }

    getDelegatedState(id: string): Observable<any> {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.register.getDelegatedState.api +
            '/' +
            id;
        return this.requestService.get(url);
    }

    checkEmailNotTaken(email: string) {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').agora.accounts.checkifEmailExists.api;
        return Observable.timer(200)
            .switchMap(_ => this.requestService.get(url, {email: email}))
            .map(res => res.data)
            .map(data => data.value);
    }

    validateCountryCodeVATPair(countryCode: string, vatNumber: string) {
        let url =
            this.configService.get('server').sahara +
            this.configService.get('endpoints').sahara.location.validateVAT.api;
        return Observable.timer(200)
            .switchMap(_ =>
                this.requestService.get(url, {countryCode: countryCode, vatNumber: vatNumber})
            )
            .map(res => res.data)
            .map(data => data.valid);
    }

    validateGSTNumber(gstNumber: string) {
        let url =
        this.configService.get('server').url +
        this.configService.get('endpoints').agora.accounts.checkGstNumber.api;
        return Observable.timer(200)
            .switchMap(_ => this.requestService.get(url, {"gst-registration-number": gstNumber}))
            .map(res => res.data)
            .map(data => data.value === null || data.value === "false");
    }

    validatePANNumber(panNumber: string) {
        let url =
        this.configService.get('server').url +
        this.configService.get('endpoints').agora.accounts.checkPanNumber.api;
        return Observable.timer(200)
            .switchMap(_ => this.requestService.get(url, {"pan-number": panNumber.toUpperCase()}))
            .map(res => res.data)
            .map(data => data.value === null || data.value === "false");
    }

    formatDict(obj: any) {
        let result = [];
        for (let prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                result.push({key: prop, value: obj[prop]});
            }
        }
        return result.sort((a, b) => a.value < b.value ? -1 : a.value > b.value ? 1 : 0);
    }

    createSupplierRegistration(invitationCode: string) {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.registration.createSupplierInvitation.api + invitationCode;
        return this.requestService.get(url);
    }

    getRegistrationTemplatesInputs(invitationCode: string) {
        let url =
            this.configService.get('server').url +
            this.configService.get('endpoints').phoenix2.registration.templateInputsSupplierInvitation.api + invitationCode;
        return this.requestService.get(url);
    }

    getGridValues(inputValues: any[], values: any[]): any[] {
        let result = [];
        let mapper = {};

        inputValues.forEach((inputValue, i) => {
            mapper[inputValue.id] = i;
            result.push({
                id: inputValue.id,
                value: [],
                label: inputValue.label,
                valueOrder: inputValue.valueOrder
            });
        });

        values.forEach(value => {
            result.forEach(_ => {
                _.value.push(value[_.id]);
            });
        });

        result.forEach(_ => {
            _.value = JSON.stringify(_.value);
        });

        return result;
    }

    getCheckboxValues(inputValues: any[], values: any[]): any[] {
        let result = [];
        inputValues.forEach((inputValue, i) => {
            result.push({
                id: inputValue.id,
                value: values[i],
                label: inputValue.label,
                valueOrder: inputValue.valueOrder
            });
        });

        return result;
    }
}



// WEBPACK FOOTER //
// ./src/app/phoenix2/register/components/register/register.service.ts